import React, { useState, useContext, useMemo, useRef } from "react";
import { Row, Col, Select, DatePicker, Card, Empty, Radio } from "antd";
import {
  // ShopOutlined,
  ArrowUpOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  InboxOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { Column, Bar } from "@ant-design/charts";
import moment from "moment";
import "moment/locale/tr";
import { DashboardEndpoints } from "@DASHBOARD";
import { AuthContext } from "@CONTEXTS";
import { useGET } from "@API2";
import "./index.scss";

moment.locale("tr");

const MIN_DATE = moment("2026-01-01");
const MAX_RANGE_DAYS = 90;

const DEFAULT_START_DATE = moment.max(
  moment().subtract(MAX_RANGE_DAYS, "days"),
  MIN_DATE.clone()
);
const DEFAULT_END_DATE = moment();


const BRANCH_BAR_COLORS = [
  "#7C3AED",
  "#8B5CF6",
  "#F97316",
  "#FB923C",
  "#FBBF24",
];

const formatNumber = (value) =>
  value == null ? "—" : Number(value).toLocaleString("tr-TR");

const buildKpiData = (metrics) => [
  {
    key: "totalDesi",
    title: "Toplam Desi",
    value: formatNumber(metrics?.totaWinnerDesi),
    variant: "purple",
    icon: "desi",
    iconColor: "#fff",
  },
  {
    key: "totalShipments",
    title: "Toplam Gönderi Adedi",
    value: formatNumber(metrics?.deliveryCount),
    variant: "orange",
    icon: "package",
    iconColor: "#fff",
  },
  {
    key: "onTimeRate",
    title: "Zamanında Teslim Oranı",
    value:
      metrics?.deliveryOnTimePercentage == null
        ? "—"
        : `${Number(metrics.deliveryOnTimePercentage).toLocaleString("tr-TR")}%`,
    variant: "light",
    icon: "clock",
    iconColor: "#7C3AED",
  },
  {
    key: "avgDeliveryDays",
    title: "Ortalama Teslim Gün Sayısı",
    value:
      metrics?.averageNumOfDeliveryDays == null
        ? "—"
        : `${Number(metrics.averageNumOfDeliveryDays).toLocaleString(
            "tr-TR"
          )} gün`,
    variant: "light",
    icon: "stopwatch",
    iconColor: "#F97316",
  },
];

const VISIT_SERIES_VISIT = "Uğrama";
const VISIT_SERIES_DELIVERED = "Teslim";

const buildVisitBranchesData = (visitTopOnTimeBranches = []) =>
  visitTopOnTimeBranches.reduce((acc, item) => {
    acc.push(
      {
        branch: item.deliveryXDockName,
        type: VISIT_SERIES_VISIT,
        value: item.count || 0,
      },
      {
        branch: item.deliveryXDockName,
        type: VISIT_SERIES_DELIVERED,
        value: item.totalDeliveredCount || 0,
      }
    );
    return acc;
  }, []);

const buildTransferDesiData = (hubs = []) =>
  hubs.map((item) => ({
    hub: item.xdockHubName,
    desi: item.totalDesi || 0,
    count: item.count || 0,
  }));

const buildTopBranchesData = (topOnTimeBranches = []) =>
  topOnTimeBranches.map((item) => ({
    branch: item.deliveryXDockName,
    rate: item.deliveryOnTimePercentage || 0,
    count: item.count || 0,
  }));

const KpiIcon = ({ type }) => {
  switch (type) {
    case "desi":
      return <AppstoreOutlined />;
    case "package":
      return <InboxOutlined />;
    case "clock":
      return <ClockCircleOutlined />;
    case "stopwatch":
      return <FieldTimeOutlined />;
    default:
      return null;
  }
};

const KpiCard = ({ item }) => (
  <Card
    className={`b2b-kpi-card b2b-kpi-card--${item.variant}`}
    bordered={false}
  >
    {item.trend && (
      <div className="b2b-kpi-card__trend">
        <ArrowUpOutlined />
        {item.trend}
      </div>
    )}
    <div
      className="b2b-kpi-card__icon"
      style={item.iconColor ? { color: item.iconColor } : undefined}
    >
      <KpiIcon type={item.icon} />
    </div>
    <div className="b2b-kpi-card__title">{item.title}</div>
    <div className="b2b-kpi-card__value">{item.value}</div>
  </Card>
);

const ChartEmpty = () => (
  <div style={{ height: 280, display: "flex", alignItems: "center" }}>
    <Empty description="Veri bulunamadı" image={Empty.PRESENTED_IMAGE_SIMPLE} />
  </div>
);

const VisitBranchesChart = React.memo(({ data }) => {
  if (!data?.length) return <ChartEmpty />;

  const config = {
    data,
    isGroup: true,
    xField: "branch",
    yField: "value",
    seriesField: "type",
    color: ({ type }) =>
      type === VISIT_SERIES_DELIVERED ? "#7C3AED" : "#F97316",
    columnStyle: { radius: [8, 8, 0, 0] },
    legend: { position: "top-right" },
    dodgePadding: 4,
    maxColumnWidth: 32,
    label: {
      position: "top",
      style: { fill: "#6B7280", fontSize: 11 },
      formatter: (datum) => Number(datum.value).toLocaleString("tr-TR"),
    },
    yAxis: {
      grid: { line: { style: { stroke: "#F0F0F0", lineDash: [4, 4] } } },
      label: {
        style: { fill: "#9CA3AF" },
        formatter: (v) => Number(v).toLocaleString("tr-TR"),
      },
    },
    xAxis: {
      label: { style: { fill: "#6B7280" }, autoHide: false, autoRotate: true },
      line: null,
      tickLine: null,
    },
    tooltip: {
      formatter: (datum) => ({
        name: datum.type,
        value: `${Number(datum.value).toLocaleString("tr-TR")} ${
          datum.type === VISIT_SERIES_DELIVERED ? "gönderi" : "kez"
        }`,
      }),
    },
    height: 280,
    autoFit: true,
  };

  return <Column {...config} />;
});

const TransferDesiChart = React.memo(({ data, color }) => {
  if (!data?.length) return <ChartEmpty />;

  const config = {
    data,
    xField: "hub",
    yField: "desi",
    color,
    columnStyle: { radius: [8, 8, 0, 0] },
    legend: false,
    maxColumnWidth: 48,
    label: {
      position: "top",
      style: { fill: "#6B7280", fontSize: 11 },
      formatter: (datum) =>
        `${Number(datum.desi).toLocaleString("tr-TR")} desi\n${Number(
          datum.count
        ).toLocaleString("tr-TR")} gönderi`,
    },
    yAxis: {
      grid: { line: { style: { stroke: "#F0F0F0", lineDash: [4, 4] } } },
      label: {
        style: { fill: "#9CA3AF" },
        formatter: (v) => Number(v).toLocaleString("tr-TR"),
      },
    },
    xAxis: {
      label: {
        style: { fill: "#6B7280", fontSize: 11 },
        autoHide: false,
        autoRotate: true,
      },
      line: null,
      tickLine: null,
    },
    tooltip: {
      customContent: (title, items) => {
        const datum = items?.[0]?.data;
        if (!datum) return null;
        return `
          <div style="padding: 8px 12px">
            <div style="margin-bottom: 6px; font-weight: 600; color: #1f2937">${title}</div>
            <div style="color: #6b7280; margin-bottom: 2px">Desi: <strong style="color:#1f2937">${Number(
              datum.desi
            ).toLocaleString("tr-TR")}</strong></div>
            <div style="color: #6b7280">Gönderi: <strong style="color:#1f2937">${Number(
              datum.count
            ).toLocaleString("tr-TR")}</strong></div>
          </div>
        `;
      },
    },
    height: 280,
    autoFit: true,
  };

  return <Column {...config} />;
});

const TopBranchesChart = React.memo(({ data }) => {
  if (!data?.length) return <ChartEmpty />;

  const chartData = [...data].reverse();
  const minRate = Math.min(...data.map((item) => item.rate), 100);
  const xMin = Math.max(0, Math.floor(minRate / 10) * 10 - 10);

  const config = {
    data: chartData,
    xField: "rate",
    yField: "branch",
    color: ({ branch }) => {
      const index = data.findIndex((item) => item.branch === branch);
      return BRANCH_BAR_COLORS[index] || BRANCH_BAR_COLORS[0];
    },
    legend: false,
    barStyle: { radius: [0, 8, 8, 0] },
    maxBarWidth: 22,
    meta: {
      rate: {
        min: xMin,
        max: 100,
      },
    },
    xAxis: {
      min: xMin,
      max: 100,
      tickInterval: 5,
      label: {
        style: { fill: "#9CA3AF" },
        formatter: (v) => `${v}%`,
      },
      grid: { line: { style: { stroke: "#F0F0F0", lineDash: [4, 4] } } },
    },
    yAxis: {
      label: { style: { fill: "#4B5563", fontSize: 12 } },
      line: null,
      tickLine: null,
    },
    label: {
      position: "right",
      style: { fill: "#6B7280", fontSize: 11 },
      formatter: (datum) => `${datum.rate}%`,
    },
    tooltip: {
      customContent: (title, items) => {
        const datum = items?.[0]?.data;
        if (!datum) return null;
        return `
          <div style="padding: 8px 12px">
            <div style="margin-bottom: 6px; font-weight: 600; color: #1f2937">${title}</div>
            <div style="color: #6b7280; margin-bottom: 2px">Zamanında Teslim: <strong style="color:#1f2937">${Number(
              datum.rate
            ).toLocaleString("tr-TR")}%</strong></div>
            <div style="color: #6b7280">Gönderi: <strong style="color:#1f2937">${Number(
              datum.count || 0
            ).toLocaleString("tr-TR")}</strong></div>
          </div>
        `;
      },
    },
    height: 420,
    autoFit: true,
  };

  return <Bar {...config} />;
});

const B2BDashboard = () => {
  const { info } = useContext(AuthContext);
  // const [dealer, setDealer] = useState("all");
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);
  const [openStartDate, setOpenStartDate] = useState(false);
  const [openEndDate, setOpenEndDate] = useState(false);
  const [transferHubType, setTransferHubType] = useState("departure");
  const [selectedMerchant] = useState(
    sessionStorage.getItem("merchantId") === "null" ||
      sessionStorage.getItem("merchantId") === null
      ? null
      : Number(sessionStorage.getItem("merchantId"))
  );
  const [channel] = useState(
    info?.onlyJetpartnerDeliveries
      ? "JETPARTNER"
      : sessionStorage.getItem("channel") === null
      ? "JETPARTNER"
      : sessionStorage.getItem("channel") === "null"
      ? null
      : sessionStorage.getItem("channel") === "INTEGRATION"
      ? "INTEGRATION"
      : sessionStorage.getItem("channel")
  );

  const queryParams =
    startDate && endDate
      ? `?merchantId=${selectedMerchant ? selectedMerchant : ""}&channel=${
          channel ? channel : ""
        }&startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format(
          "YYYY-MM-DD"
        )}`
      : false;

  const { data: deliveryMetrics, loading: deliveryMetricsLoading } = useGET(
    DashboardEndpoints.DeliveryMetrics,
    queryParams
  );
  const { data: topOnTimeBranches, loading: topOnTimeBranchesLoading } = useGET(
    DashboardEndpoints.TopOnTimeBranches,
    queryParams
  );
  const {
    data: visitTopOnTimeBranches,
    loading: visitTopOnTimeBranchesLoading,
  } = useGET(DashboardEndpoints.VisitTopOnTimeBranches, queryParams);
  const {
    data: deliveriesGroupedByDepartureXDockHub,
    loading: deliveriesGroupedByDepartureXDockHubLoading,
  } = useGET(
    DashboardEndpoints.DeliveriesGroupedByDepartureXDockHub,
    queryParams
  );
  const {
    data: deliveriesGroupedByArrivalXDockHub,
    loading: deliveriesGroupedByArrivalXDockHubLoading,
  } = useGET(
    DashboardEndpoints.DeliveriesGroupedByArrivalXDockHub,
    queryParams
  );

  const loading =
    deliveryMetricsLoading ||
    topOnTimeBranchesLoading ||
    visitTopOnTimeBranchesLoading ||
    deliveriesGroupedByDepartureXDockHubLoading ||
    deliveriesGroupedByArrivalXDockHubLoading;

  const lastDataRef = useRef(null);
  const hasFreshData = !!startDate && !!endDate && !loading;
  const freshDashboardData = {
    deliveryMetrics:
      deliveryMetrics &&
      !Array.isArray(deliveryMetrics) &&
      typeof deliveryMetrics === "object"
        ? deliveryMetrics
        : {},
    topOnTimeBranches: Array.isArray(topOnTimeBranches)
      ? topOnTimeBranches
      : [],
    visitTopOnTimeBranches: Array.isArray(visitTopOnTimeBranches)
      ? visitTopOnTimeBranches
      : [],
    deliveriesGroupedByDepartureXDockHub: Array.isArray(
      deliveriesGroupedByDepartureXDockHub
    )
      ? deliveriesGroupedByDepartureXDockHub
      : [],
    deliveriesGroupedByArrivalXDockHub: Array.isArray(
      deliveriesGroupedByArrivalXDockHub
    )
      ? deliveriesGroupedByArrivalXDockHub
      : [],
  };

  if (hasFreshData) {
    lastDataRef.current = freshDashboardData;
  }

  const dashboardData = hasFreshData
    ? freshDashboardData
    : lastDataRef.current;

  const kpiData = useMemo(
    () => buildKpiData(dashboardData?.deliveryMetrics),
    [dashboardData?.deliveryMetrics]
  );
  const visitBranchesData = useMemo(
    () => buildVisitBranchesData(dashboardData?.visitTopOnTimeBranches || []),
    [dashboardData?.visitTopOnTimeBranches]
  );
  const transferDesiData = useMemo(
    () =>
      buildTransferDesiData(
        transferHubType === "departure"
          ? dashboardData?.deliveriesGroupedByDepartureXDockHub || []
          : dashboardData?.deliveriesGroupedByArrivalXDockHub || []
      ),
    [
      transferHubType,
      dashboardData?.deliveriesGroupedByDepartureXDockHub,
      dashboardData?.deliveriesGroupedByArrivalXDockHub,
    ]
  );
  const topBranchesData = useMemo(
    () => buildTopBranchesData(dashboardData?.topOnTimeBranches || []),
    [dashboardData?.topOnTimeBranches]
  );

  const periodLabel =
    startDate && endDate
      ? `${startDate.format("YYYY-MM-DD")} — ${endDate.format("YYYY-MM-DD")}`
      : "—";

  if (loading) {
    return <Select disabled loading placeholder="Yükleniyor..." />;
  }

  return (
    <div className="app-dashboard">
      <div className="b2b-dashboard">
        <div className="b2b-dashboard__header">
          <h1 className="b2b-dashboard__title">JetPartner'e Hoş Geldiniz!</h1>
          <Card className="b2b-dashboard__filters" bordered={false}>
            <div className="b2b-dashboard__filters-inner">
              <span className="b2b-dashboard__filters-label">FİLTRELER</span>
              {/* <div className="b2b-dashboard__dealer">
                <ShopOutlined className="b2b-dashboard__dealer-icon" />
                <Select
                  className="b2b-dashboard__dealer-select"
                  value={dealer}
                  onChange={setDealer}
                  options={DEALER_OPTIONS}
                />
              </div> */}
              <DatePicker
                className="b2b-dashboard__date-picker"
                placeholder="Başlangıç tarihi"
                format="DD MMM YYYY"
                allowClear
                open={openStartDate}
                onOpenChange={setOpenStartDate}
                value={startDate}
                disabledDate={(current) => {
                  if (!current) return false;
                  return (
                    current.isBefore(MIN_DATE, "day") ||
                    (endDate &&
                      current.isBefore(
                        moment(endDate).add(-MAX_RANGE_DAYS, "days"),
                        "day"
                      )) ||
                    (endDate && current.isAfter(moment(endDate), "day"))
                  );
                }}
                onChange={(val) => {
                  setStartDate(val);
                  if (!endDate) {
                    setEndDate(val);
                    setOpenEndDate(true);
                  } else if (
                    val &&
                    endDate.diff(val, "days") > MAX_RANGE_DAYS
                  ) {
                    setEndDate(moment(val).add(MAX_RANGE_DAYS, "days"));
                  }
                }}
              />
              <DatePicker
                className="b2b-dashboard__date-picker"
                placeholder="Bitiş tarihi"
                format="DD MMM YYYY"
                allowClear
                open={openEndDate}
                onOpenChange={setOpenEndDate}
                value={endDate}
                disabledDate={(current) => {
                  if (!current) return false;
                  return (
                    current.isBefore(MIN_DATE, "day") ||
                    (startDate && current.isBefore(moment(startDate), "day")) ||
                    (startDate &&
                      current.isAfter(
                        moment(startDate).add(MAX_RANGE_DAYS, "days"),
                        "day"
                      ))
                  );
                }}
                onChange={(val) => {
                  if (!startDate) {
                    setStartDate(val);
                    setOpenStartDate(true);
                  }
                  setEndDate(val);
                }}
              />
            </div>
          </Card>
        </div>

        <p className="b2b-dashboard__period">
          Gösterilen dönem: {periodLabel}
        </p>

        <Row gutter={[16, 16]} className="b2b-dashboard__kpis">
          {kpiData.map((item) => (
            <Col xs={24} sm={12} lg={6} key={item.key}>
              <KpiCard item={item} />
            </Col>
          ))}
        </Row>

        <Row gutter={[16, 16]} className="b2b-dashboard__charts">
          <Col xs={24} lg={16}>
            <Card
              className="b2b-dashboard__chart-card"
              bordered={false}
              title={
                <div className="b2b-dashboard__chart-heading">
                  <h3>Uğrama & Teslim Performansı</h3>
                  <p>Uğrama sayısı ve teslim edilen gönderi adetleri</p>
                </div>
              }
            >
              <VisitBranchesChart data={visitBranchesData} />
            </Card>

            <Card
              className="b2b-dashboard__chart-card b2b-dashboard__chart-card--spaced"
              bordered={false}
              title={
                <div className="b2b-dashboard__chart-heading">
                  <h3>Transfer Merkezine Göre Desi</h3>
                  <p>
                    {transferHubType === "departure"
                      ? "Çıkış transfer merkezlerine göre toplam desi dağılımı"
                      : "Varış transfer merkezlerine göre toplam desi dağılımı"}
                  </p>
                </div>
              }
              extra={
                <Radio.Group
                  className="b2b-dashboard__hub-switch"
                  value={transferHubType}
                  onChange={(e) => setTransferHubType(e.target.value)}
                  buttonStyle="solid"
                  size="small"
                >
                  <Radio.Button value="departure">Çıkış</Radio.Button>
                  <Radio.Button value="arrival">Varış</Radio.Button>
                </Radio.Group>
              }
            >
              <TransferDesiChart
                data={transferDesiData}
                color={
                  transferHubType === "departure" ? "#F97316" : "#7C3AED"
                }
              />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              className="b2b-dashboard__chart-card b2b-dashboard__chart-card--tall"
              bordered={false}
              title={
                <div className="b2b-dashboard__chart-heading">
                  <h3>En İyi 5 Şube</h3>
                  <p>Teslim performansı (zamanında teslim %)</p>
                </div>
              }
            >
              <TopBranchesChart data={topBranchesData} />
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default React.memo(B2BDashboard);
